import { parseAbsoluteToLocal } from "@internationalized/date"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedSessionLookupItem } from "ipctg-inflow-types/InFlowDB"
import { PromoFailReason } from "ipctg-inflow-types/InFlowEnums"
import {
  GatedPromoRegisterPOSTRequest,
  GatedPromoRegisterPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowGatedAPI"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { formatZonedDateTimeWithTimeZone } from "@/utilities/etc"
import { GatedPromoRegisterError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { RATES_HREF } from "@/utilities/globals"
import { sessionDetailsQueryOptions } from "@/utilities/hooks/queries/express"
import { setupIntentQueryOptions } from "@/utilities/hooks/queries/payment"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizedAPILicensePlate } from "@/utilities/normalizers"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/cart"
)({
  component: RouteComponent,
})

export interface DiscountCodeSchema {
  promoCode: string
}

function RouteComponent() {
  const { sessionID } = Route.useParams()
  const { reregister } = Route.useSearch()
  const queryClient = useQueryClient()
  const navigate = Route.useNavigate()
  const { t } = useTranslation()
  const { data: sessionRsp } = useSuspenseQuery(
    sessionDetailsQueryOptions({ sessionID })
  )

  const [showDiscountCodeField, toggleShowDiscountCodeField] = useToggle(false)
  const [discountCodeApplied, setDiscountCodeApplied] = useState("")
  const form = useForm<DiscountCodeSchema>({
    defaultValues: {
      promoCode: "",
    },
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.cart.header")}
      </Heading>
      <Step step={3} of={4} />
      <Card>
        <Heading level={1} as={2} className="center">
          {t("register.cart.subhead")}
        </Heading>
        <p className="body--2 center">
          {!discountCodeApplied && (
            <Button
              hue="primary"
              variant="solid"
              presentation="link"
              onPress={() => {
                if (showDiscountCodeField) {
                  setDiscountCodeApplied("")
                }
                toggleShowDiscountCodeField()
              }}
            >
              {showDiscountCodeField
                ? t("register.cart.promoCode.remove")
                : t("register.cart.promoCode.add")}
            </Button>
          )}
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {showDiscountCodeField && !discountCodeApplied && (
            <fieldset disabled={isPending}>
              <Controller<DiscountCodeSchema>
                name="promoCode"
                control={form.control}
                rules={{ required: requiredRule(t("errors.common.required")) }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(v.toUpperCase())}
                    isInvalid={invalid}
                    aria-label={t("register.cart.promoCode.fieldLabel")}
                  >
                    <div className="promoCodeField">
                      <TextField.Input innerRef={ref} style={{ width: 0 }} />
                      <Button
                        type="submit"
                        hue="neutral"
                        variant="default"
                        isPending={isPending}
                        style={{ width: "fit-content", flexShrink: 0 }}
                      >
                        {t("register.cart.promoCode.apply")}
                      </Button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </fieldset>
          )}
          {discountCodeApplied && (
            <Text level={2} className="highlighted-cell center">
              <b>{discountCodeApplied}</b>{" "}
              {t("register.cart.promoCode.successfullyApplied")}
            </Text>
          )}
        </form>
        <LinkButton
          from={Route.fullPath}
          to="/qr/$qrLookupID/gated/register/$sessionID/checkout"
          search={{ reregister }}
          hue="secondary"
          variant="default"
        >
          <Icon name="creditCard" slot="left" />
          {t("register.cart.submit")}
        </LinkButton>
        <PaymentMethod />
      </Card>
      {!reregister && (
        <>
          <PayingCashLauncher
            sessionID={sessionID}
            onSuccess={() =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
              })
            }
          />
          <Separator orientation="horizontal" />
        </>
      )}
      <div>
        <Heading level={2}>{t("register.cart.sessionDetails.header")}</Heading>
        <Text level={2} style={{ marginTop: "var(--space-2)" }}>
          {t("register.cart.sessionDetails.body")}{" "}
          <a href={RATES_HREF} target="_blank">
            {t("common.seeRates")}.
          </a>
        </Text>
      </div>
      {sessionRsp && <SessionDetails session={sessionRsp.session} />}
    </>
  )

  function onSubmit(data: DiscountCodeSchema) {
    mutate(data)
  }

  async function mutationFn(request: GatedPromoRegisterPOSTRequest) {
    const rsp = await api.promoRegister.POST(request)
    if ("failReason" in rsp) {
      const shouldThrow = [
        PromoFailReason.InvalidRequestFormat,
        PromoFailReason.SystemError,
        PromoFailReason.Unauthorized,
      ]
      const error = new GatedPromoRegisterError(rsp.failReason)
      if (shouldThrow.some((r) => r === rsp.failReason)) {
        throw error
      }
      form.setError("promoCode", { message: t(error.translationKey) })
      return
    }
    return rsp
  }

  function onSuccess(
    rsp: GatedPromoRegisterPOSTSuccessResponse | undefined,
    variables: GatedPromoRegisterPOSTRequest
  ) {
    if (!rsp) return
    if (rsp.bypassCC) {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey! },
      })
    } else {
      queryClient.removeQueries({ ...setupIntentQueryOptions() })
      setDiscountCodeApplied(variables.promoCode)
    }
  }
}

function PaymentMethod() {
  const { t } = useTranslation()
  return (
    <div>
      <Text level={3} className="center">
        {t("register.cart.payments.accepted")}
      </Text>
      <div className="payment-methods">
        <Icon name="paymentApplePay" retainOriginalColors />
        <Icon name="paymentGooglePay" retainOriginalColors />
        <Icon name="paymentVisa" retainOriginalColors />
        <Icon name="paymentMastercard" retainOriginalColors />
        <Icon name="paymentAmericanExpress" retainOriginalColors />
        <Icon name="paymentDiscover" retainOriginalColors />
        <Text level={3}>+ {t("register.cart.payments.more")}</Text>
      </div>
    </div>
  )
}

function SessionDetails(props: { session: GatedSessionLookupItem }) {
  const { t } = useTranslation()
  return (
    <dl>
      <div>
        <dt>
          <b>{t("register.cart.sessionDetails.list.licensePlate")}</b>
        </dt>
        <dd>{normalizedAPILicensePlate(props.session.cameraPlate)}</dd>
      </div>
      <div>
        <dt>{t("register.cart.sessionDetails.list.lot")}</dt>
        <dd>{props.session.lotName}</dd>
      </div>
      <div>
        <dt>{t("register.cart.sessionDetails.list.entryTime")}</dt>
        <dd>
          {formatZonedDateTimeWithTimeZone(
            parseAbsoluteToLocal(props.session.startTime)
          )}
        </dd>
      </div>
    </dl>
  )
}
